import { useMemo } from 'react';
import { TypographyProps, ContainerStyleProps, PaywallButton } from '@minecraft.atoms';
import { IconProps } from '@minecraft.icon';
import { DiscountTypeEnum, PlanTermEnum } from '@minecraft.graphql-operations';
import { PlanType } from '../../usePlans';
import { TranslateFn, useTranslation } from '../../../I18N';
import { priceFormatter } from '../../helpers/priceFormatter';

type ModalConfigValues = {
  offerText: string;
  offerTextProps: TypographyProps;
  offerSubText1?: string;
  offerSubText2?: string;
  highlightColor: TypographyProps['color'];
  headerIcon: string;
  bgColor: ContainerStyleProps['bgColor'];
  iconColor: IconProps['themeColor'];
  buttonColor?: React.ComponentProps<typeof PaywallButton>['color'];
  discountBadgeText?: string;
  discountBadgeIcon?: string;
  planFeatures?: string[];
};

const VALID_DISCOUNT_TYPES = [
  DiscountTypeEnum.CanadianPremiumDiscount,
  DiscountTypeEnum.NeverPaidCohortDiscount,
  DiscountTypeEnum.PremiumDiscountOffer,
];

const MODAL_CONFIG: ({ t }: { t: TranslateFn }) => Record<DiscountTypeEnum, ModalConfigValues> = ({ t }) => ({
  [DiscountTypeEnum.NeverPaidCohortDiscount]: {
    offerText: t('common:paywall.plan.never_paid_discount_offer'),
    offerTextProps: { fontSize: 'xl', fontWeight: 'semibold' },
    offerSubText1: t('common:paywall.plan.never_paid_discount_offer_subtext1'),
    offerSubText2: t('common:paywall.plan.never_paid_discount_offer_subtext2'),
    highlightColor: 'discountHighlight',
    headerIcon: 'never-paid-asset',
    bgColor: 'discountOfferBg',
    iconColor: 'discountOffer',
    buttonColor: 'discount',
    planFeatures: [
      t('common:paywall.features.premium.storageUnlimited'),
      t('common:paywall.features.premium.trackSubmissions'),
      t('common:paywall.features.premium.plan.thriveBenefits'),
    ],
  },
  [DiscountTypeEnum.CanadianPremiumDiscount]: {
    offerText: t('common:paywall.plan.special_offer_canada'),
    offerTextProps: { fontSize: 'h2', fontWeight: 'semibold' },
    highlightColor: 'discountHighlightRed',
    headerIcon: 'canadaDiscount-asset',
    bgColor: 'discountOfferBgRed',
    iconColor: 'discountOfferRed',
    buttonColor: 'discountRed',
    discountBadgeText: t('common:paywall.plan.special_offer_canada_tagline'),
    discountBadgeIcon: 'canada-icon',
    planFeatures: [
      t('common:paywall.features.premium.storageUnlimited'),
      t('common:paywall.features.premium.trackSubmissions'),
      t('common:paywall.features.premium.connectSocial'),
    ],
  },
  [DiscountTypeEnum.PremiumDiscountOffer]: {
    offerText: t('common:paywall.plan.special_offer'),
    offerTextProps: { fontSize: 'xl', fontWeight: 'semibold' },
    highlightColor: 'discountHighlight',
    headerIcon: 'discount-asset',
    bgColor: 'discountOfferBg',
    iconColor: 'discountOffer',
    buttonColor: 'discount',
    planFeatures: [
      t('common:paywall.features.premium.storageUnlimited'),
      t('common:paywall.features.premium.trackSubmissions'),
      t('common:paywall.features.premium.connectSocial'),
    ],
  },
});

export const useDiscountHook = ({ plans }: { plans?: PlanType[] }) => {
  const { t } = useTranslation();
  const discountedPlan = plans?.find((plan) => Boolean(plan?.discountPercent && plan?.discountType));

  const originalPrice = priceFormatter({
    price: Number(discountedPlan?.strikeThroughPrice),
    t,
    roundingFloor: true,
  });
  const annualPrice = priceFormatter({
    price: Number(discountedPlan?.discountedPrice),
    currencyCode: discountedPlan?.currencyCode,
    t,
    roundingFloor: true,
  });
  const price = priceFormatter({
    price:
      discountedPlan?.term?.code === PlanTermEnum.Annual
        ? Number(discountedPlan?.discountedPrice) / 12
        : Number(discountedPlan?.discountedPrice),
    currencyCode: discountedPlan?.currencyCode,
    t,
    roundingFloor: true,
  });

  const billedText = useMemo(() => {
    if (discountedPlan?.totalBillingCycles)
      return t('common:paywall.plan.billed_text_with_billing_cycles', {
        months: discountedPlan.totalBillingCycles,
        price: originalPrice,
      });
    if (discountedPlan?.term.code === PlanTermEnum.Annual)
      return `${annualPrice} ${t('common:paywall.plan.billed_yearly')}`;
    return '';
  }, [discountedPlan, t, originalPrice, annualPrice]);

  const themeValues = useMemo(() => {
    const themes = MODAL_CONFIG({ t });
    const activeTheme = VALID_DISCOUNT_TYPES.includes(discountedPlan?.discountType)
      ? discountedPlan?.discountType
      : DiscountTypeEnum.PremiumDiscountOffer;
    return themes[activeTheme];
  }, [discountedPlan?.discountType, t]);

  return useMemo(
    () => ({
      ...themeValues,
      originalPrice,
      price,
      billedText,
      discountedPlan,
    }),
    [themeValues, originalPrice, price, billedText, discountedPlan]
  );
};
